'use client';

import { Plus } from 'lucide-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useEditorStore } from '@/lib/editor/editorStore';
import { getFontEntry } from '@/lib/editor/fonts';
import { TEXT_PRESETS, presetToLayerPatch, type TextPreset } from '@/lib/editor/textPresets';

const TEXT_CATEGORIES = ['Все', 'Заголовки', 'Подписи', 'Акценты'] as const;

function presetBadgePreview(preset: TextPreset): CSSProperties {
  if (preset.layerType !== 'sticker') return {};
  return {
    background: preset.badgeAccentColor
      ? `linear-gradient(135deg, ${preset.backgroundColor}, ${preset.badgeAccentColor})`
      : preset.backgroundColor,
    border: preset.badgeBorderWidth
      ? `1.5px solid ${preset.badgeBorderColor ?? 'transparent'}`
      : undefined,
    borderRadius: preset.badgeStyle === 'pill' ? 999 : preset.badgeStyle === 'soft-square' ? 5 : 8,
    padding: '5px 9px',
    boxShadow: '0 7px 18px rgba(0,0,0,.3)',
  };
}

/**
 * Left "Текст" tool panel (editor V2 rewrite, task 8; presets added in
 * Phase 3E task 4).
 *
 * Ported from the `VideoEditor.tsx` monolith's `activeTool === 'text'`
 * branch (~line 1803): a description + "Добавить текст" button that
 * creates a default text layer via `addTextLayer`. The monolith's inline
 * `TextLayerControls` (color/font/position for the *selected* layer) is
 * NOT ported here — that consolidated property editing already lives in
 * the right-side `EditorDetailsPanel` (task 6), so this panel only needs
 * the "add" affordance.
 *
 * Below that: a curated "Стили" grid of Canva-like text presets
 * (`textPresets.ts`). Each chip previews the preset's own look. Clicking
 * one either restyles the currently selected text/sticker layer (keeping
 * its text/position/timing) or, if nothing is selected, creates a new
 * layer via `addPresetLayer`.
 */
export function TextPanel() {
  const { t } = useI18n();
  const addTextLayer = useEditorStore((s) => s.addTextLayer);
  const addPresetLayer = useEditorStore((s) => s.addPresetLayer);
  const updateLayer = useEditorStore((s) => s.updateLayer);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const layers = useEditorStore((s) => s.layers);
  const textLayerCount = useEditorStore((s) => s.layers.filter((l) => l.type === 'text').length);
  const [category, setCategory] = useState<(typeof TEXT_CATEGORIES)[number]>('Все');

  const selectedLayer = layers.find((l) => l.id === selectedLayerId) ?? null;
  const canApplyToSelected = selectedLayer?.type === 'text' || selectedLayer?.type === 'sticker';
  const presets = useMemo(
    () => category === 'Все' ? TEXT_PRESETS : TEXT_PRESETS.filter((preset) => preset.category === category),
    [category]
  );

  function applyPreset(preset: TextPreset) {
    if (canApplyToSelected && selectedLayer) {
      updateLayer(selectedLayer.id, presetToLayerPatch(preset));
    } else {
      addPresetLayer(preset);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolText')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        <p className="text-xs leading-5 text-white/60">{t('studio.editor.textDescription')}</p>

        <button
          type="button"
          onClick={addTextLayer}
          className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-xs font-black text-white shadow-[0_16px_34px_rgba(91,53,255,0.2)] transition hover:bg-brand-500"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('studio.editor.addText')}
        </button>

        {textLayerCount > 0 && (
          <p className="mt-3 rounded-xl bg-white/5 px-3 py-2.5 text-[11px] leading-5 text-white/50">
            {t('studio.editor.controlsSection')}: {textLayerCount} — {t('studio.editor.selectLayerToEdit')}
          </p>
        )}

        {canApplyToSelected && selectedLayer && (
          <div className="mt-3 rounded-xl border border-brand-500/30 bg-brand-500/10 px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-200">Выбранный текст</p>
            <p className="mt-1 truncate text-[11px] text-white/60">{selectedLayer.text}</p>
            <p className="mt-1 text-[10px] leading-4 text-white/35">Шрифт, размер и цвет — в панели настроек справа.</p>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/40">Текстовые шаблоны</p>
          <span className="text-[10px] text-white/30">{TEXT_PRESETS.length}</span>
        </div>

        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {TEXT_CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`shrink-0 rounded-full px-2.5 py-1.5 text-[10px] font-bold transition ${
                category === item ? 'bg-brand-500 text-white' : 'bg-white/[0.06] text-white/50 hover:text-white'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          {presets.map((preset) => {
            const font = getFontEntry(preset.fontFamily);
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className="group overflow-hidden rounded-xl border border-white/10 bg-white/[0.035] text-left transition hover:border-brand-500/80 hover:bg-white/[0.07]"
              >
                <span
                  className="flex h-[76px] items-center justify-center overflow-hidden border-b border-white/10 px-2 text-center"
                  style={{ background: preset.previewBackground ?? '#19191c' }}
                >
                  <span
                    className="line-clamp-2 max-w-full whitespace-pre-line text-sm leading-[0.95]"
                    style={{
                      fontFamily: font.cssVar,
                      fontWeight: preset.fontWeight,
                      fontStyle: preset.fontStyle,
                      color: preset.color,
                      ...presetBadgePreview(preset),
                    }}
                  >
                    {preset.defaultText ?? preset.name}
                  </span>
                </span>
                <span className="block px-2.5 py-2">
                  <span className="block truncate text-[10px] font-bold text-white/85">{preset.name}</span>
                  <span className="mt-0.5 block truncate text-[9px] text-white/35">{preset.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
